// MeetingPageRtc.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Typography, Container, Button, IconButton, Avatar, Chip,
  Snackbar, Alert, Grid, CircularProgress, Card, CardContent, TextField,
  useMediaQuery, useTheme
} from "@mui/material";
import {
  Mic, MicOff, Videocam, VideocamOff, ScreenShare, StopScreenShare,
  CallEnd, People, Chat, Link as LinkIcon, MoreVert,
  VolumeUp, VolumeOff, Filter, Palette,
  Fullscreen, FullscreenExit // أضف هذه
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import io from "socket.io-client";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import logo from "../images/logo.jpg";

const COLOR_SCHEME = {
  primary: "#2563eb",
  primaryLight: "#3b82f6",
  primaryDark: "#1d4ed8",
  secondary: "#f59e0b",
  accent: "#10b981",
  background: "#0f172a",
  surface: "#1e293b",
  text: "#f1f5f9",
  success: "#22c55e",
  warning: "#f59e0b",
  error: "#ef4444",
  info: "#3b82f6",
};

// ===== اضبط دول حسب بيئتك =====
const API_BASE_URL = "https://filesregsiteration.sstli.com/erp";
const SIGNALING_URL = "https://0ae9f6774e47.ngrok-free.app";
const ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }];

// Timeout constants
const INIT_TIMEOUT = 15000;
const API_TIMEOUT = 10000;

// Video filters
const VIDEO_FILTERS = {
  none: "none",
  blur: "blur(2px)",
  warm: "sepia(0.3) saturate(1.4)",
  cool: "hue-rotate(180deg) saturate(1.2)",
  vintage: "contrast(1.1) sepia(0.3)",
  sharp: "contrast(1.2) saturate(1.1)"
};

const MeetingPage = ({ meetingId: propMeetingId }) => {
  const { meetingId: urlMeetingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getMeetingId = () => {
    return propMeetingId || urlMeetingId || getMeetingIdFromUrl();
  };

  // حالة عامة
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initializationFailed, setInitializationFailed] = useState(false);

  // واجهة
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [showParticipants, setShowParticipants] = useState(!isMobile);
  const [showChat, setShowChat] = useState(false);
  const [videoLayout, setVideoLayout] = useState("grid");
  const [videoFilter, setVideoFilter] = useState("none");

  // ميديا محلية
  const [isAudioOn, setIsAudioOn] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  // سوكيت و WebRTC
  const socketRef = useRef(null);
  const peersRef = useRef(new Map());
  const sendersRef = useRef(new Map());
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const initTimeoutRef = useRef(null);

  // ستريمات للعرض
  const [remotePeers, setRemotePeers] = useState([]);
  const [participants, setParticipants] = useState([]);

  // محادثة
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);

  // عداد الوقت
  const [meetingTime, setMeetingTime] = useState(0);
  const meetingStartTimeRef = useRef(null);

  const getMeetingIdFromUrl = () => {
    const pathParts = window.location.pathname.split("/");
    const id = pathParts[pathParts.length - 1] || pathParts[pathParts.length - 2];
    return id;
  };

  const getUserFromURL = () => {
    try {
      const userParam = searchParams.get('user');
      if (userParam) {
        const decodedUserData = decodeURIComponent(atob(userParam));
        const user = JSON.parse(decodedUserData);
        return user;
      }
    } catch (error) {
      console.error("Error parsing user data from URL:", error);
    }
    return null;
  };

  const timeoutPromise = (ms, promise, errorMsg = "Timeout error") => {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(errorMsg));
      }, ms);

      promise.then(
        (res) => {
          clearTimeout(timeoutId);
          resolve(res);
        },
        (err) => {
          clearTimeout(timeoutId);
          reject(err);
        }
      );
    });
  };

  const validateMeetingId = () => {
    const meetingId = getMeetingId();
    if (!meetingId || meetingId === "undefined" || meetingId === "null") {
      setError("معرف الاجتماع غير صالح");
      showSnackbar("معرف الاجتماع غير صالح", "error");
      return false;
    }
    return true;
  };

  // دالة لتحويل الثواني إلى تنسيق ساعات:دقائق:ثواني
  const formatMeetingTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // useEffect لعداد الوقت
  useEffect(() => {
    let intervalId;
    
    if (!loading && !error && !initializationFailed && meetingInfo) {
      meetingStartTimeRef.current = Date.now();
      
      intervalId = setInterval(() => {
        setMeetingTime(prevTime => prevTime + 1);
      }, 1000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [loading, error, initializationFailed, meetingInfo]);

  // تعديل الريسبونسف على الموبايل
  useEffect(() => {
    if (isMobile) {
      setShowParticipants(false);
      setShowChat(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const userFromURL = getUserFromURL();

    if (userFromURL) {
      setCurrentUser(userFromURL);
    } else {
      const userData = localStorage.getItem("user");
      if (!userData) {
        showSnackbar("يجب تسجيل الدخول أولاً", "error");
        setError("يجب تسجيل الدخول أولاً");
        setLoading(false);
        return;
      }
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
      } catch {
        showSnackbar("خطأ في قراءة المستخدم", "error");
        setError("خطأ في قراءة بيانات المستخدم");
        setLoading(false);
      }
    }
  }, [searchParams]);

  const handleLeave = () => {
    console.log("Cleaning up meeting resources...");

    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }

    const meetingId = getMeetingId();

    if (socketRef.current) {
      try {
        socketRef.current.emit("leave-meeting", {
          meetingId,
          user: { guid: currentUser?.guid },
        });
      } catch (error) {
        console.warn("Error emitting leave-meeting:", error);
      }

      try {
        socketRef.current.disconnect();
      } catch (error) {
        console.warn("Error disconnecting socket:", error);
      }
    }

    try {
      localStreamRef.current?.getTracks().forEach(t => t.stop());
    } catch (error) {
      console.warn("Error stopping local stream:", error);
    }

    try {
      screenStreamRef.current?.getTracks().forEach(t => t.stop());
    } catch (error) {
      console.warn("Error stopping screen stream:", error);
    }

    for (const id of peersRef.current.keys()) {
      const pc = peersRef.current.get(id);
      try {
        pc?.getSenders()?.forEach(s => s?.track && s.track.stop && s.track.stop());
      } catch (error) {
        console.warn("Error stopping peer senders:", error);
      }
      try {
        pc?.close();
      } catch (error) {
        console.warn("Error closing peer connection:", error);
      }
    }
    peersRef.current.clear();
    sendersRef.current.clear();
  };

  const syncLocalTracksToPeers = () => {
    if (!localStreamRef.current) return;

    const audioTrack = localStreamRef.current.getAudioTracks()[0] || null;
    const videoTrack = localStreamRef.current.getVideoTracks()[0] || null;

    for (const [peerId, pc] of peersRef.current.entries()) {
      let senders = sendersRef.current.get(peerId);
      if (!senders) {
        senders = { audioSender: null, videoSender: null };
        sendersRef.current.set(peerId, senders);
      }

      if (audioTrack) {
        if (senders.audioSender) {
          if (senders.audioSender.track !== audioTrack) {
            senders.audioSender.replaceTrack(audioTrack).catch(() => {});
          }
        } else {
          try {
            const aSender = pc.addTrack(audioTrack, localStreamRef.current);
            senders.audioSender = aSender;
          } catch (e) {
            console.warn("error adding audio track", e);
          }
        }
      }

      if (videoTrack) {
        if (senders.videoSender) {
          if (senders.videoSender.track !== videoTrack) {
            senders.videoSender.replaceTrack(videoTrack).catch(() => {});
          }
        } else {
          try {
            const vSender = pc.addTrack(videoTrack, localStreamRef.current);
            senders.videoSender = vSender;
          } catch (e) {
            console.warn("error adding video track", e);
          }
        }
      }
    }
  };

  useEffect(() => {
    if (!currentUser) return;

    const init = async () => {
      const meetingId = getMeetingId();

      if (!validateMeetingId()) {
        setLoading(false);
        setInitializationFailed(true);
        return;
      }

      initTimeoutRef.current = setTimeout(() => {
        if (loading) {
          console.error("Initialization timeout reached");
          setError("تجاوز الوقت المحدد لتجهيز الاجتماع. حاول تحديث الصفحة.");
          setLoading(false);
          setInitializationFailed(true);
          showSnackbar("تجاوز الوقت المحدد لتجهيز الاجتماع", "error");
        }
      }, INIT_TIMEOUT);

      try {
        setLoading(true);
        setError(null);
        setInitializationFailed(false);

        let meetingData = null;
        try {
          const res = await timeoutPromise(
            API_TIMEOUT,
            fetch(`${API_BASE_URL}/meeting_room_api.php?action=get_meeting&meeting_id=${meetingId}`),
            "API request timeout"
          );

          if (res.ok) {
            meetingData = await res.json();
            setMeetingInfo(meetingData);
          } else {
            console.warn("Failed to fetch meeting data, status:", res.status);
            setMeetingInfo(null);
            setError("لم يتم العثور على الاجتماع أو انتهت صلاحيته");
            setInitializationFailed(true);
          }
        } catch (apiError) {
          console.warn("API error:", apiError);
          setMeetingInfo(null);
          setError("فشل في تحميل بيانات الاجتماع");
          setInitializationFailed(true);
        }

        if (initializationFailed) {
          clearTimeout(initTimeoutRef.current);
          setLoading(false);
          return;
        }

        console.log("Requesting media permissions...");
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: { width: { ideal: 1280 }, height: { ideal: 720 } }
          });
          localStreamRef.current = stream;

          setTrackEnabled(stream, "audio", false);
          setTrackEnabled(stream, "video", false);

          syncLocalTracksToPeers();
        } catch (err) {
          console.warn("getUserMedia error, continuing without media:", err);
          showSnackbar("تعذّر الوصول للكاميرا/المايك، هتكمل الاجتماع بدون ميديا", "warning");
        }

        console.log("Connecting to signaling server...");
        const socket = io(SIGNALING_URL, {
          transports: ["websocket", "polling"],
          timeout: 10000,
          reconnectionAttempts: 3,
          reconnectionDelay: 1000
        });

        socketRef.current = socket;

        socket.on("connect", () => {
          console.log("Socket connected successfully");
          clearTimeout(initTimeoutRef.current);

          socket.emit("join-meeting", {
            meetingId,
            user: {
              guid: currentUser.guid,
              name: currentUser.userName || currentUser.fullName || "User",
              avatar: (currentUser.userName || currentUser.fullName || "U").charAt(0),
              isAudioOn: false,
              isVideoOn: false,
            },
          });
        });

        socket.on("connect_error", (error) => {
          console.error("Socket connection error:", error);
          setError("فشل الاتصال بخادم الاجتماع. حاول تحديث الصفحة.");
          setLoading(false);
          setInitializationFailed(true);
          showSnackbar("فشل الاتصال بخادم الاجتماع", "error");
        });

        socket.on("disconnect", (reason) => {
          console.log("Socket disconnected:", reason);
          if (reason === "io server disconnect") {
            showSnackbar("تم فصل الاتصال من الخادم", "error");
          }
        });

        socket.on("room-peers", async (list) => {
          console.log("Received room peers:", list);
          try {
            for (const p of list) {
              await createPeerAndOffer(p.socketId, p.user);
            }
            const me = buildSelfParticipant(socket.id);
            const others = list.map((p) => toParticipant(p.user, p.socketId));
            setParticipants(mergeParticipants([me, ...others]));
          } catch (error) {
            console.error("Error processing room peers:", error);
          }
        });

        socket.on("user-joined", ({ user, socketId }) => {
          console.log("User joined:", user);
          setParticipants((prev) => mergeParticipants([...prev, toParticipant(user, socketId)]));
        });

        socket.on("offer", async ({ offer, sender }) => {
          console.log("Received offer from:", sender);
          try {
            await ensurePeer(sender, null);
            syncLocalTracksToPeers();

            const pc = peersRef.current.get(sender);
            await pc.setRemoteDescription(new RTCSessionDescription(offer));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            socket.emit("answer", { answer: pc.localDescription, target: sender });
          } catch (error) {
            console.error("Error handling offer:", error);
          }
        });

        socket.on("answer", async ({ answer, sender }) => {
          const pc = peersRef.current.get(sender);
          if (pc) await pc.setRemoteDescription(new RTCSessionDescription(answer));
        });

        socket.on("ice-candidate", async ({ candidate, sender }) => {
          const pc = peersRef.current.get(sender);
          if (!pc) return;
          try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch { }
        });

        socket.on("user-media-updated", ({ userId, audio, video }) => {
          setParticipants((prev) =>
            prev.map((p) =>
              (p.guid === userId || p.socketId === userId) ? { ...p, isAudioOn: audio, isVideoOn: video } : p
            )
          );
        });

        socket.on("user-left", ({ socketId }) => {
          cleanupPeer(socketId);
          setParticipants((prev) => prev.filter((p) => p.socketId !== socketId));
          setRemotePeers((prev) => prev.filter((p) => p.peerId !== socketId));
        });

        // ====== REAL-TIME CHAT EVENTS ======
        socket.on("chat-message", (messageData) => {
          console.log("Received chat message:", messageData);
          setChatMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              sender: messageData.sender,
              time: new Date().toLocaleTimeString(),
              message: messageData.message,
            },
          ]);
        });

        socket.on("user-typing", (data) => {
          // ممكن تضيف مؤشر typing هنا
          console.log("User typing:", data);
        });

        setTimeout(() => {
          setLoading(false);
        }, 2000);

      } catch (err) {
        console.error("Meeting init failed:", err);
        setError("فشل في تجهيز الاجتماع");
        setLoading(false);
        setInitializationFailed(true);
        showSnackbar("فشل إعداد الاجتماع", "error");
      }
    };

    init();

    return () => {
      try {
        window.removeEventListener("beforeunload", handleLeave);
      } catch { }
      handleLeave();
    };
  }, [currentUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const buildSelfParticipant = (socketId) => ({
    socketId,
    guid: currentUser?.guid,
    name: currentUser?.userName || currentUser?.fullName || "أنا",
    avatar: (currentUser?.userName || currentUser?.fullName || "U").charAt(0),
    isAudioOn,
    isVideoOn,
    isHost: false,
    isSelf: true,
  });

  const toParticipant = (user, socketId) => ({
    socketId,
    guid: user?.guid,
    name: user?.name ?? "ضيف",
    avatar: user?.avatar ?? "U",
    isAudioOn: user?.isAudioOn ?? true,
    isVideoOn: user?.isVideoOn ?? true,
    isHost: user?.isHost ?? false,
    isSelf: false,
  });

  const mergeParticipants = (arr) => {
    const seen = new Map();
    for (const p of arr) seen.set(p.socketId || p.guid || p.name, p);
    return Array.from(seen.values());
  };

  const ensurePeer = async (peerId, userObj) => {
    if (peersRef.current.has(peerId)) return peersRef.current.get(peerId);

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    peersRef.current.set(peerId, pc);

    sendersRef.current.set(peerId, { audioSender: null, videoSender: null });

    const remoteStream = new MediaStream();

    pc.ontrack = (e) => {
      e.streams[0].getTracks().forEach((t) => remoteStream.addTrack(t));
      setRemotePeers((prev) => {
        const idx = prev.findIndex((p) => p.peerId === peerId);
        const entry = { peerId, user: userObj, stream: remoteStream };
        if (idx === -1) return [...prev, entry];
        const clone = [...prev];
        clone[idx] = entry;
        return clone;
      });
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        socketRef.current?.emit("ice-candidate", { target: peerId, candidate: e.candidate });
      }
    };

    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      console.log("connection state:", peerId, st);

      if (st === "failed" || st === "closed") {
        cleanupPeer(peerId);
      }
    };

    return pc;
  };

  const createPeerAndOffer = async (peerId, userObj) => {
    const pc = await ensurePeer(peerId, userObj);
    syncLocalTracksToPeers();

    const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
    await pc.setLocalDescription(offer);
    socketRef.current?.emit("offer", { offer, target: peerId });
  };

  const cleanupPeer = (peerId) => {
    const pc = peersRef.current.get(peerId);

    try {
      pc?.close();
    } catch {}

    peersRef.current.delete(peerId);
    sendersRef.current.delete(peerId);
  };

  const setTrackEnabled = (stream, kind, enabled) => {
    stream.getTracks().filter(t => t.kind === kind).forEach(t => (t.enabled = enabled));
  };

  const replaceVideoTrackForAll = (newTrack) => {
    for (const [, senders] of sendersRef.current.entries()) {
      const sender = senders?.videoSender;
      if (sender && newTrack) {
        try { sender.replaceTrack(newTrack); } catch { }
      }
    }
  };

  const toggleAudio = async () => {
    if (!localStreamRef.current) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: { width: { ideal: 1280 }, height: { ideal: 720 } }
        });
        localStreamRef.current = stream;
        setTrackEnabled(stream, "video", isVideoOn);
        syncLocalTracksToPeers();
      } catch (err) {
        console.error("getUserMedia on toggleAudio:", err);
        showSnackbar("تعذّر الوصول للمايك", "error");
        return;
      }
    }

    const next = !isAudioOn;
    setIsAudioOn(next);
    if (localStreamRef.current) setTrackEnabled(localStreamRef.current, "audio", next);
    socketRef.current?.emit("toggle-media", {
      meetingId: getMeetingId(),
      userId: currentUser?.guid || socketRef.current?.id,
      audio: next,
      video: isVideoOn,
    });
    setParticipants((prev) => prev.map((p) => (p.isSelf ? { ...p, isAudioOn: next } : p)));
  };

  const toggleVideo = async () => {
    if (!localStreamRef.current) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: { width: { ideal: 1280 }, height: { ideal: 720 } }
        });
        localStreamRef.current = stream;
        setTrackEnabled(stream, "audio", isAudioOn);
        syncLocalTracksToPeers();
      } catch (err) {
        console.error("getUserMedia on toggleVideo:", err);
        showSnackbar("تعذّر الوصول للكاميرا", "error");
        return;
      }
    }

    const next = !isVideoOn;
    setIsVideoOn(next);
    if (localStreamRef.current) setTrackEnabled(localStreamRef.current, "video", next);
    socketRef.current?.emit("toggle-media", {
      meetingId: getMeetingId(),
      userId: currentUser?.guid || socketRef.current?.id,
      audio: isAudioOn,
      video: next,
    });
    setParticipants((prev) => prev.map((p) => (p.isSelf ? { ...p, isVideoOn: next } : p)));
  };

  const startScreenShare = async () => {
    try {
      const scr = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      screenStreamRef.current = scr;
      const screenTrack = scr.getVideoTracks()[0];
      setIsScreenSharing(true);
      replaceVideoTrackForAll(screenTrack);
      screenTrack.onended = () => stopScreenShare();
    } catch {
      showSnackbar("تم إلغاء مشاركة الشاشة", "info");
    }
  };

  const stopScreenShare = () => {
    if (!screenStreamRef.current) return;
    screenStreamRef.current.getTracks().forEach((t) => t.stop());
    screenStreamRef.current = null;
    setIsScreenSharing(false);
    const camTrack = localStreamRef.current?.getVideoTracks()[0];
    if (camTrack) replaceVideoTrackForAll(camTrack);
  };

  const toggleScreenShare = () => (isScreenSharing ? stopScreenShare() : startScreenShare());

  const leaveMeeting = () => {
    handleLeave();
    navigate("/meetings");
  };

  const copyMeetingLink = () => {
    if (meetingInfo?.meetingLink) {
      navigator.clipboard.writeText(meetingInfo.meetingLink);
      showSnackbar("تم نسخ رابط الاجتماع", "success");
    }
  };

  // ====== REAL-TIME CHAT FUNCTIONS ======
// ====== REAL-TIME CHAT FUNCTIONS ======
const sendMessage = () => {
  if (!newMessage.trim()) return;
  
  const messageData = {
    sender: currentUser?.userName || currentUser?.fullName || "أنا",
    message: newMessage.trim(),
    timestamp: Date.now(),
    meetingId: getMeetingId()
  };

  // أرسل الرسالة للخادم علشان يوصلها للكل
  socketRef.current?.emit("chat-message", messageData);

  // ما تضيفهاش محلي هنا - خليها تجي من السيرفر
  setNewMessage("");
};

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const handleCloseSnackbar = () =>
    setSnackbar((s) => ({ ...s, open: false }));

  const retryInitialization = () => {
    setLoading(true);
    setError(null);
    setInitializationFailed(false);
  };

  const toggleVideoLayout = () => {
    setVideoLayout(videoLayout === "grid" ? "spotlight" : "grid");
  };

  const localVideoEl = useMemo(() => {
    return (
      <VideoTile
        key="local"
        name={currentUser?.userName || currentUser?.fullName || "أنا"}
        avatar={(currentUser?.userName || currentUser?.fullName || "U").charAt(0)}
        stream={localStreamRef.current}
        isAudioOn={isAudioOn}
        isVideoOn={isVideoOn}
        isSelf
        videoFilter={videoFilter}
        layout={videoLayout}
        isMobile={isMobile}
      />
    );
  }, [isAudioOn, isVideoOn, currentUser, videoFilter, videoLayout, isMobile]);

  const remoteVideoTiles = participants
    .filter((p) => !p.isSelf)
    .map((p) => {
      const peer = remotePeers.find((r) => r.peerId === p.socketId);
      return (
        <VideoTile
          key={p.socketId || p.guid || p.name}
          name={p.name}
          avatar={p.avatar}
          stream={peer?.stream || null}
          isAudioOn={p.isAudioOn}
          isVideoOn={!!peer?.stream && p.isVideoOn}
          videoFilter={videoFilter}
          layout={videoLayout}
          isMobile={isMobile}
        />
      );
    });

  if (loading) {
    return (
      <Box sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${COLOR_SCHEME.background} 0%, #1e293b 100%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: 3
      }}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress
            sx={{
              color: COLOR_SCHEME.primary,
              width: 80,
              height: 80,
              animationDuration: '1.5s'
            }}
          />
          <Box sx={{
            position: 'absolute',
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${COLOR_SCHEME.primary} 0%, transparent 70%)`,
            opacity: 0.3
          }} />
        </Box>
        <Typography variant="h5" sx={{ color: COLOR_SCHEME.text, fontWeight: 600, textAlign: 'center', px: 2 }}>
          جاري تجهيز الاجتماع...
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8', opacity: 0.8, textAlign: 'center' }}>
          Meeting ID: {getMeetingId()}
        </Typography>
      </Box>
    );
  }

  if (initializationFailed || error) {
    return (
      <Box sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${COLOR_SCHEME.background} 0%, #1e293b 100%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: 3, p: 3
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant={isMobile ? "h5" : "h4"} color="error" sx={{ fontWeight: 700, mb: 2 }}>
            {error || "فشل في تحميل الاجتماع"}
          </Typography>
          <Typography variant="body1" sx={{ color: '#94a3b8', textAlign: 'center', maxWidth: 500, lineHeight: 1.6 }}>
            {!getMeetingId() ? "لم يتم تحديد معرف الاجتماع" : "تعذر الوصول إلى الاجتماع. قد يكون المعرف غير صحيح أو انتهت صلاحية الاجتماع."}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={retryInitialization}
            sx={{
              bgcolor: COLOR_SCHEME.primary,
              px: 4,
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              '&:hover': {
                bgcolor: COLOR_SCHEME.primaryDark,
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            إعادة المحاولة
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/meetings")}
            sx={{
              borderColor: COLOR_SCHEME.primary,
              color: COLOR_SCHEME.primary,
              px: 4,
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              '&:hover': {
                borderColor: COLOR_SCHEME.primaryLight,
                bgcolor: alpha(COLOR_SCHEME.primary, 0.1),
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            العودة إلى الاجتماعات
          </Button>
        </Box>
      </Box>
    );
  }

  if (!meetingInfo) {
    return (
      <Box sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${COLOR_SCHEME.background} 0%, #1e293b 100%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: 2, p: 2
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} color="error" sx={{ fontWeight: 600, textAlign: 'center' }}>
          لم يتم العثور على الاجتماع
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/meetings")}
          sx={{
            bgcolor: COLOR_SCHEME.primary,
            px: 4,
            borderRadius: 2,
            fontWeight: 600,
            '&:hover': {
              bgcolor: COLOR_SCHEME.primaryDark
            }
          }}
        >
          العودة إلى الاجتماعات
        </Button>
      </Box>
    );
  }

  const allVideos = [localVideoEl, ...remoteVideoTiles];

  return (
    <Box sx={{
      minHeight: "100vh",
      bgcolor: COLOR_SCHEME.background,
      background: `linear-gradient(135deg, ${COLOR_SCHEME.background} 0%, #1e293b 100%)`
    }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: 2,
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            backdropFilter: 'blur(10px)',
            bgcolor: alpha(COLOR_SCHEME.surface, 0.9)
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{
        bgcolor: alpha(COLOR_SCHEME.surface, 0.8),
        backdropFilter: 'blur(20px)',
        p: isMobile ? 1 : 2,
        borderBottom: `1px solid ${alpha('#ffffff', 0.1)}`,
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <Container maxWidth="xl">
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: isMobile ? 1 : 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img
                  src={logo}
                  alt="SSTLI Logo"
                  style={{
                    width: isMobile ? 32 : 40,
                    height: isMobile ? 32 : 40,
                    borderRadius: 8,
                    objectFit: 'cover'
                  }}
                />
                {!isSmallMobile && (
                  <Typography variant={isMobile ? "body1" : "h6"} sx={{ color: "white", fontWeight: "bold" }}>
                    SSTLI
                  </Typography>
                )}
              </Box>
              <Box sx={{ maxWidth: isMobile ? 120 : 'none' }}>
                <Typography 
                  variant={isMobile ? "body2" : "h6"} 
                  sx={{ 
                    color: "white", 
                    fontWeight: "bold",
                    fontSize: isMobile ? '0.8rem' : '1rem',
                    lineHeight: 1.2
                  }}
                >
                  {meetingInfo.title}
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: isMobile ? '0.7rem' : '0.8rem' }}>
                  {meetingInfo.organizer}
                </Typography>
              </Box>
            </Box>
            
            {/* عداد الوقت */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 0.5 : 2,
              flexWrap: 'wrap'
            }}>
              {!isSmallMobile && (
                <Chip
                  icon={<Typography sx={{ fontSize: isMobile ? '1rem' : '1.2rem' }}>⏱️</Typography>}
                  label={`${formatMeetingTime(meetingTime)}`}
                  sx={{
                    bgcolor: COLOR_SCHEME.primary,
                    color: "white",
                    fontWeight: 600,
                    borderRadius: 2,
                    fontSize: isMobile ? '0.7rem' : '0.9rem',
                    px: 1,
                    '& .MuiChip-icon': {
                      mr: 0.5
                    }
                  }}
                  size="small"
                />
              )}
              
              <Chip
                label={`${participants.length || (1 + remotePeers.length)}`}
                sx={{
                  bgcolor: COLOR_SCHEME.accent,
                  color: "white",
                  fontWeight: 600,
                  borderRadius: 2,
                  fontSize: isMobile ? '0.7rem' : '0.8rem'
                }}
                size="small"
              />
              
              <IconButton
                onClick={copyMeetingLink}
                sx={{
                  color: "#94a3b8",
                  "&:hover": {
                    color: "white",
                    bgcolor: alpha(COLOR_SCHEME.primary, 0.2)
                  },
                  borderRadius: 2,
                  p: isMobile ? 0.5 : 1
                }}
                size={isMobile ? "small" : "medium"}
              >
                <LinkIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ 
        py: isMobile ? 1 : 3, 
        height: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 140px)',
        px: isMobile ? 1 : 3
      }}>
        <Grid container spacing={isMobile ? 1 : 3} sx={{ height: '100%' }}>
          {/* Video Grid */}
          <Grid item xs={12} lg={showParticipants || showChat ? 8 : 12} sx={{ height: '100%' }}>
            <Box sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? 1 : 2
            }}>
              {/* Video Filters Bar - مخفي على الموبايل الصغير */}
              {!isSmallMobile && (
                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  bgcolor: alpha(COLOR_SCHEME.surface, 0.5),
                  p: isMobile ? 1 : 1.5,
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)',
                  flexWrap: 'wrap'
                }}>
                  <Filter sx={{ color: '#94a3b8', fontSize: isMobile ? 16 : 20 }} />
                  <Typography variant="body2" sx={{ color: '#94a3b8', mr: 1, fontSize: isMobile ? '0.7rem' : '0.8rem' }}>
                    فلتر:
                  </Typography>
                  {Object.entries(VIDEO_FILTERS).map(([key]) => (
                    <Chip
                      key={key}
                      label={key === 'none' ? 'عادي' : isMobile ? key.charAt(0) : key}
                      onClick={() => setVideoFilter(key)}
                      variant={videoFilter === key ? "filled" : "outlined"}
                      sx={{
                        bgcolor: videoFilter === key ? COLOR_SCHEME.primary : 'transparent',
                        color: videoFilter === key ? 'white' : '#94a3b8',
                        borderColor: videoFilter === key ? COLOR_SCHEME.primary : '#374151',
                        borderRadius: 2,
                        fontSize: isMobile ? '0.6rem' : '0.75rem',
                        '&:hover': {
                          bgcolor: alpha(COLOR_SCHEME.primary, 0.2)
                        }
                      }}
                      size="small"
                    />
                  ))}
                </Box>
              )}

              {/* Videos Container */}
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': {
                  background: COLOR_SCHEME.primary,
                  borderRadius: 2
                }
              }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: isMobile ? 1 : 2,
                    justifyContent: 'center',
                    alignItems: 'flex-start'
                  }}
                >
                  {allVideos.map((video, index) => (
                    <Box
                      key={index}
                      sx={{
                        flex: isMobile ? '1 1 100%' : '0 0 320px',
                        maxWidth: isMobile ? '100%' : '320px',
                        height: isMobile ? 200 : 200,
                        minWidth: isMobile ? '100%' : 'auto'
                      }}
                    >
                      {video}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Sidebar */}
          {(showParticipants || showChat) && (
            <Grid item xs={12} lg={4} sx={{ height: '100%' }}>
              <Box sx={{
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? 1 : 2,
                height: '100%'
              }}>

                {/* Participants Panel */}
                {showParticipants && (
                  <Card sx={{
                    bgcolor: alpha(COLOR_SCHEME.surface, 0.8),
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha('#ffffff', 0.1)}`,
                    borderRadius: 2,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    flex: showChat ? 1 : 'none'
                  }}>
                    <CardContent sx={{ 
                      p: isMobile ? 1 : 2, 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column' 
                    }}>
                      <Typography variant={isMobile ? "body1" : "h6"} sx={{
                        color: "white",
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontWeight: 600,
                        fontSize: isMobile ? '0.9rem' : '1rem'
                      }}>
                        <People sx={{ 
                          color: COLOR_SCHEME.primary, 
                          fontSize: isMobile ? 18 : 24 
                        }} />
                        المشاركون ({participants.length})
                      </Typography>
                      <Box sx={{
                        flex: 1,
                        overflowY: "auto",
                        pr: 0.5,
                        '&::-webkit-scrollbar': {
                          width: 4,
                        },
                        '&::-webkit-scrollbar-track': {
                          background: alpha('#ffffff', 0.1),
                          borderRadius: 2,
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: COLOR_SCHEME.primary,
                          borderRadius: 2,
                        }
                      }}>
                        {participants.map((participant) => (
                          <ParticipantRow
                            key={participant.socketId || participant.guid}
                            name={participant.name}
                            avatar={participant.avatar}
                            isAudioOn={participant.isAudioOn}
                            isVideoOn={participant.isVideoOn}
                            isSelf={participant.isSelf}
                            isMobile={isMobile}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Chat Panel */}
                {showChat && (
                  <Card sx={{
                    bgcolor: alpha(COLOR_SCHEME.surface, 0.8),
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha('#ffffff', 0.1)}`,
                    borderRadius: 2,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    flex: showParticipants ? 1 : 'none',
                    height: showParticipants ? '50%' : '100%'
                  }}>
                    <CardContent sx={{
                      p: isMobile ? 1 : 2,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column"
                    }}>
                      <Typography variant={isMobile ? "body1" : "h6"} sx={{
                        color: "white",
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontWeight: 600,
                        fontSize: isMobile ? '0.9rem' : '1rem'
                      }}>
                        <Chat sx={{ 
                          color: COLOR_SCHEME.primary, 
                          fontSize: isMobile ? 18 : 24 
                        }} />
                        المحادثة
                      </Typography>

                      {/* Messages */}
                      <Box sx={{
                        flexGrow: 1,
                        overflowY: "auto",
                        mb: 1,
                        pr: 0.5,
                        '&::-webkit-scrollbar': {
                          width: 4,
                        },
                        '&::-webkit-scrollbar-track': {
                          background: alpha('#ffffff', 0.1),
                          borderRadius: 2,
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: COLOR_SCHEME.primary,
                          borderRadius: 2,
                        }
                      }}>
                        {chatMessages.map((msg) => (
                          <Box
                            key={msg.id}
                            sx={{
                              mb: 1,
                              p: isMobile ? 1 : 2,
                              bgcolor: alpha(COLOR_SCHEME.primary, 0.1),
                              border: `1px solid ${alpha(COLOR_SCHEME.primary, 0.2)}`,
                              borderRadius: 1,
                              backdropFilter: 'blur(10px)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                bgcolor: alpha(COLOR_SCHEME.primary, 0.15),
                                transform: 'translateX(2px)'
                              }
                            }}
                          >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography variant="body2" sx={{
                                color: COLOR_SCHEME.primary,
                                fontWeight: "bold",
                                fontSize: isMobile ? '0.7rem' : '0.9rem'
                              }}>
                                {msg.sender}
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                color: "#94a3b8", 
                                fontSize: isMobile ? '0.6rem' : '0.75rem' 
                              }}>
                                {msg.time}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{
                              color: "white",
                              mt: 0.5,
                              lineHeight: 1.4,
                              fontSize: isMobile ? '0.7rem' : '0.9rem'
                            }}>
                              {msg.message}
                            </Typography>
                          </Box>
                        ))}
                        <div ref={messagesEndRef} />
                      </Box>

                      {/* Message Input */}
                      <Box sx={{ display: "flex", gap: 0.5, alignItems: 'flex-end' }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="اكتب رسالة..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          multiline
                          maxRows={2}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              color: "white",
                              borderRadius: 1,
                              fontSize: isMobile ? '0.8rem' : '0.9rem',
                              "& fieldset": {
                                borderColor: alpha('#ffffff', 0.2),
                              },
                              "&:hover fieldset": {
                                borderColor: COLOR_SCHEME.primary,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: COLOR_SCHEME.primary,
                              },
                            },
                            "& .MuiInputBase-input": {
                              fontSize: isMobile ? '0.8rem' : '0.9rem',
                              '&::placeholder': {
                                color: '#94a3b8',
                                opacity: 1
                              }
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={sendMessage}
                          sx={{
                            bgcolor: COLOR_SCHEME.primary,
                            borderRadius: 1,
                            px: isMobile ? 1 : 2,
                            minWidth: 'auto',
                            height: '40px',
                            '&:hover': {
                              bgcolor: COLOR_SCHEME.primaryDark,
                              transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.3s ease',
                            fontSize: isMobile ? '0.7rem' : '0.8rem'
                          }}
                        >
                          إرسال
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Controls */}
      <Box sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: alpha(COLOR_SCHEME.surface, 0.95),
        backdropFilter: 'blur(20px)',
        borderTop: `1px solid ${alpha('#ffffff', 0.1)}`,
        p: isMobile ? 1 : 2
      }}>
        <Container maxWidth="xl">
          <Box sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: isMobile ? 0.5 : 2,
            flexWrap: "wrap"
          }}>

            {/* Audio Control */}
            <IconButton
              onClick={toggleAudio}
              sx={{
                bgcolor: isAudioOn ? COLOR_SCHEME.primary : COLOR_SCHEME.error,
                color: "white",
                width: isMobile ? 48 : 56,
                height: isMobile ? 48 : 56,
                borderRadius: 2,
                "&:hover": {
                  bgcolor: isAudioOn ? COLOR_SCHEME.primaryDark : "#dc2626",
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease',
                boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
              }}
            >
              {isAudioOn ? <Mic fontSize={isMobile ? "small" : "medium"} /> : <MicOff fontSize={isMobile ? "small" : "medium"} />}
            </IconButton>

            {/* Video Control */}
            <IconButton
              onClick={toggleVideo}
              sx={{
                bgcolor: isVideoOn ? COLOR_SCHEME.primary : COLOR_SCHEME.error,
                color: "white",
                width: isMobile ? 48 : 56,
                height: isMobile ? 48 : 56,
                borderRadius: 2,
                "&:hover": {
                  bgcolor: isVideoOn ? COLOR_SCHEME.primaryDark : "#dc2626",
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease',
                boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
              }}
            >
              {isVideoOn ? <Videocam fontSize={isMobile ? "small" : "medium"} /> : <VideocamOff fontSize={isMobile ? "small" : "medium"} />}
            </IconButton>

            {/* Screen Share - مخفي على الموبايل الصغير */}
            {!isSmallMobile && (
              <IconButton
                onClick={toggleScreenShare}
                sx={{
                  bgcolor: isScreenSharing ? COLOR_SCHEME.warning : alpha('#ffffff', 0.1),
                  color: "white",
                  width: isMobile ? 48 : 56,
                  height: isMobile ? 48 : 56,
                  borderRadius: 2,
                  "&:hover": {
                    bgcolor: isScreenSharing ? "#d97706" : alpha('#ffffff', 0.2),
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
                }}
              >
                {isScreenSharing ? <StopScreenShare fontSize={isMobile ? "small" : "medium"} /> : <ScreenShare fontSize={isMobile ? "small" : "medium"} />}
              </IconButton>
            )}

            {/* Participants Toggle */}
            <IconButton
              onClick={() => setShowParticipants(!showParticipants)}
              sx={{
                bgcolor: showParticipants ? COLOR_SCHEME.primary : alpha('#ffffff', 0.1),
                color: "white",
                width: isMobile ? 48 : 56,
                height: isMobile ? 48 : 56,
                borderRadius: 2,
                "&:hover": {
                  bgcolor: showParticipants ? COLOR_SCHEME.primaryDark : alpha('#ffffff', 0.2),
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease',
                boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
              }}
            >
              <People fontSize={isMobile ? "small" : "medium"} />
            </IconButton>

            {/* Chat Toggle */}
            <IconButton
              onClick={() => setShowChat(!showChat)}
              sx={{
                bgcolor: showChat ? COLOR_SCHEME.primary : alpha('#ffffff', 0.1),
                color: "white",
                width: isMobile ? 48 : 56,
                height: isMobile ? 48 : 56,
                borderRadius: 2,
                "&:hover": {
                  bgcolor: showChat ? COLOR_SCHEME.primaryDark : alpha('#ffffff', 0.2),
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease',
                boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
              }}
            >
              <Chat fontSize={isMobile ? "small" : "medium"} />
            </IconButton>

            {/* Leave Meeting */}
            <Button
              variant="contained"
              startIcon={<CallEnd />}
              onClick={leaveMeeting}
              sx={{
                bgcolor: COLOR_SCHEME.error,
                "&:hover": {
                  bgcolor: "#dc2626",
                  transform: 'translateY(-2px)'
                },
                px: isMobile ? 2 : 4,
                py: isMobile ? 1 : 1.5,
                fontWeight: "bold",
                borderRadius: 2,
                height: isMobile ? 48 : 56,
                transition: 'all 0.3s ease',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                '& .MuiButton-startIcon': {
                  mr: isMobile ? 1 : 2, // زودت المارجين هنا
                },
                fontSize: isMobile ? '0.8rem' : '0.9rem'
              }}
            >
              {isMobile ? 'إنهاء' : 'إنهاء المكالمة'}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

// ====== Components ======
const VideoTile = ({ name, avatar, stream, isAudioOn, isVideoOn, isSelf = false, videoFilter = "none", layout = "grid", isMobile = false }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // دالة للدخول والخروج من وضع fullscreen
  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        // دخول fullscreen
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        // خروج من fullscreen
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
      // Fallback للأنظمة القديمة
      if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      }
    }
  };

  // استمع لتغيرات وضع fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (stream) {
      if (v.srcObject !== stream) v.srcObject = stream;
      const playSafe = async () => {
        try {
          await v.play();
        } catch {
          // المتصفح ممكن يمنع بدون تفاعل
        }
      };
      playSafe();
    } else {
      try { v.pause(); } catch { }
    }
  }, [stream]);

  const filterStyle = VIDEO_FILTERS[videoFilter] || VIDEO_FILTERS.none;

  return (
    <Card
      ref={containerRef}
      sx={{
        bgcolor: "#000000",
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
        border: `2px solid ${alpha('#ffffff', 0.1)}`,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: isMobile ? "none" : "translateY(-4px)",
          borderColor: alpha('#3b82f6', 0.5),
          boxShadow: '0 10px 30px rgba(0,0,0,0.4)'
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        
        // ====== إضافة الـ styles للfullscreen هنا ======
        '&:fullscreen': {
          width: '100vw',
          height: '100vh',
          borderRadius: 0,
          bgcolor: '#000',
          '& video': {
            objectFit: 'contain',
            width: '100%',
            height: '100%'
          },
          '& .MuiCardContent-root': {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }
        },
        '&:-webkit-full-screen': {
          width: '100vw',
          height: '100vh',
          borderRadius: 0,
          bgcolor: '#000',
          '& video': {
            objectFit: 'contain',
            width: '100%',
            height: '100%'
          },
          '& .MuiCardContent-root': {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }
        },
        // ====== نهاية إضافة الـ styles ======
      }}
    >
      <CardContent sx={{
        p: 0,
        flex: 1,
        position: "relative",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: "#000000",
      }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isSelf}
          style={{
            width: "100%",
            height: "100%",
            objectFit: isFullscreen ? "contain" : "cover",
            background: "#000000",
            filter: filterStyle,
            borderRadius: isFullscreen ? 0 : 'inherit',
            display: stream && isVideoOn ? "block" : "none",
          }}
        />

        {(!stream || !isVideoOn) && (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#1e293b",
              background: `linear-gradient(135deg, #1e293b 0%, #334155 100%)`,
            }}
          >
            <Avatar
              sx={{
                width: isMobile ? 60 : (layout === 'grid' ? 80 : 120),
                height: isMobile ? 60 : (layout === 'grid' ? 80 : 120),
                bgcolor: COLOR_SCHEME.primary,
                fontSize: isMobile ? "1.5rem" : (layout === 'grid' ? "2rem" : "3rem"),
                fontWeight: 'bold',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
              }}
            >
              {avatar}
            </Avatar>
          </Box>
        )}

        {/* زر Fullscreen */}
        <IconButton
          onClick={toggleFullscreen}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            bgcolor: "rgba(0,0,0,0.7)",
            color: "white",
            backdropFilter: 'blur(10px)',
            width: isMobile ? 32 : 40,
            height: isMobile ? 32 : 40,
            borderRadius: 1,
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.9)",
              transform: 'scale(1.1)'
            },
            transition: 'all 0.3s ease',
            zIndex: 10
          }}
          size="small"
        >
          {isFullscreen ? (
            <FullscreenExit fontSize={isMobile ? "small" : "medium"} />
          ) : (
            <Fullscreen fontSize={isMobile ? "small" : "medium"} />
          )}
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "rgba(0,0,0,0.7)",
            backdropFilter: 'blur(10px)',
            px: isMobile ? 1 : 2,
            py: isMobile ? 0.8 : 1.2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body2" sx={{
            color: "white",
            fontWeight: 600,
            fontSize: isMobile ? '0.7rem' : '0.9rem'
          }}>
            {name} {isSelf ? "(أنا)" : ""}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {!isAudioOn && (
              <MicOff sx={{ fontSize: isMobile ? 12 : 16, color: COLOR_SCHEME.error }} />
            )}
            {!isVideoOn && (
              <VideocamOff sx={{ fontSize: isMobile ? 12 : 16, color: COLOR_SCHEME.warning }} />
            )}
          </Box>
        </Box>

        {!isVideoOn && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              bgcolor: "rgba(0,0,0,0.7)",
              color: "white",
              px: 1,
              py: 0.3,
              borderRadius: 1,
              fontSize: isMobile ? '0.6rem' : '0.75rem',
              fontWeight: 600,
              backdropFilter: 'blur(10px)'
            }}
          >
            📷 متوقف
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const ParticipantRow = ({ name, avatar, isAudioOn, isVideoOn, isSelf, isMobile = false }) => {
  return (
    <Box sx={{
      bgcolor: alpha('#ffffff', 0.05),
      border: `1px solid ${alpha('#ffffff', 0.1)}`,
      borderRadius: 1,
      p: isMobile ? 1 : 2,
      mb: 1,
      transition: 'all 0.3s ease',
      '&:hover': {
        bgcolor: alpha(COLOR_SCHEME.primary, 0.1),
        borderColor: alpha(COLOR_SCHEME.primary, 0.3),
        transform: isMobile ? 'none' : 'translateX(4px)'
      },
      backdropFilter: 'blur(10px)'
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 2 }}>
        <Avatar sx={{
          bgcolor: COLOR_SCHEME.primary,
          width: isMobile ? 36 : 44,
          height: isMobile ? 36 : 44,
          fontWeight: 'bold',
          fontSize: isMobile ? '0.8rem' : '1rem',
          boxShadow: '0 3px 10px rgba(0,0,0,0.3)'
        }}>
          {avatar}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" sx={{
            color: "white",
            fontWeight: 600,
            fontSize: isMobile ? '0.7rem' : '0.9rem'
          }}>
            {name} {isSelf ? "(أنا)" : ""}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              {isAudioOn ? (
                <VolumeUp sx={{ fontSize: isMobile ? 12 : 16, color: COLOR_SCHEME.success }} />
              ) : (
                <VolumeOff sx={{ fontSize: isMobile ? 12 : 16, color: COLOR_SCHEME.error }} />
              )}
              <Typography variant="caption" sx={{
                color: isAudioOn ? COLOR_SCHEME.success : COLOR_SCHEME.error,
                fontSize: isMobile ? '0.6rem' : '0.75rem'
              }}>
                {isAudioOn ? "مفعل" : "مطفأ"}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              {isVideoOn ? (
                <Videocam sx={{ fontSize: isMobile ? 12 : 16, color: COLOR_SCHEME.success }} />
              ) : (
                <VideocamOff sx={{ fontSize: isMobile ? 12 : 16, color: COLOR_SCHEME.warning }} />
              )}
              <Typography variant="caption" sx={{
                color: isVideoOn ? COLOR_SCHEME.success : COLOR_SCHEME.warning,
                fontSize: isMobile ? '0.6rem' : '0.75rem'
              }}>
                {isVideoOn ? "مفعل" : "مطفأ"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MeetingPage;